import React, { FC, useEffect } from "react";

// Hooks
import { PaymentFlowLayout } from "../../../Components/Layouts/PaymentFlowLayout/PaymentFlowLayout";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { usePayFlow } from "@modules/PaymentProcessing/hooks/usePayFlow";
import { PaymentMethodSelect } from "@modules/PaymentProcessing/Components/PaymentMethodSelect/PaymentMethodSelect";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { trackEvent } from "@Libs/GrowthBook/services/growthbook";

export const ChoosePaymentMethod: FC = () => {
  const {
    state: { operationSummary, allowedPaymentMethods, enabledPaymentMethods, relatedServiceId },
    navigateToStep,
    onPageMount,
    backRoute,
  } = usePayFlow();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { serviceOffer } = useServiceOffer(relatedServiceId);

  const handlePaymentMethodSelect = (paymentMethod: PaymentMethodType) => {
    navigateToStep("CONFIGURE_PAYMENT", {
      queryParams: {
        ...backRoute.queryParams,
        paymentMethod,
      },
    });
  };

  useEffect(() => {
    onPageMount?.("CHOOSE_PAYMENT_METHOD");
  }, []);

  useEffect(() => {
    if (!currentUser || !serviceOffer) return;
    trackEvent("payment_method_page_visited", {
      contractId: relatedServiceId,
      serviceRequestId: serviceOffer.serviceRequestId,
      clientId: currentUser.id,
      clientEmail: currentUser.email,
      isSubscribed: currentUser.subscribed ?? false,
    });
  }, [currentUser, serviceOffer]);

  const handleBack = () => {
    navigate(backRoute.to, backRoute);
  };

  return (
    <PaymentFlowLayout>
      <PaymentFlowLayout.Nav>
        <BackLink onClick={handleBack} />
      </PaymentFlowLayout.Nav>
      <PaymentFlowLayout.OperationSummary>
        {operationSummary}
      </PaymentFlowLayout.OperationSummary>
      <PaymentFlowLayout.Header>
        <FormattedMessage {...messages.chooseYourPaymentMethod} />
      </PaymentFlowLayout.Header>
      <PaymentFlowLayout.Content>
        <PaymentMethodSelect
          onSelect={handlePaymentMethodSelect}
          allowedPaymentMethods={allowedPaymentMethods}
          enabledPaymentMethods={enabledPaymentMethods}
        />
      </PaymentFlowLayout.Content>
    </PaymentFlowLayout>
  );
};
